// src/components/DoctorAvailabilityEditor.jsx
//
// Shared editor for a doctor's OR a navigator's real availability — weekly
// recurring hours plus date-specific overrides (a day off, or custom hours
// for one date). `kind` picks doctorAvailabilityAPI vs navigatorAvailabilityAPI;
// `mode` picks which half of that API to call ('self' needs no id — the
// backend resolves it from the JWT — 'staff' needs `doctorId`, only used for
// kind="doctor" today; navigator editing is self-service only, see
// NavigatorAvailabilityController's javadoc for why no {navigatorId} staff
// family exists yet). Kept the doctor-centric name/backend table names
// (doctor_availability_*) rather than renaming everything generic — see
// V133__navigator_availability.sql's migration comment for why.
//
// Layout: a page head (eyebrow / title / subtitle, with the Save action) above
// a two-column grid — weekly hours on the left, a weekly-total summary and a
// tip on the right — then the exceptions card and the change history. Weekly
// hours are edited locally and saved together by the head's "Save changes"
// button, which stays disabled until something differs from what was last
// loaded or saved. Exceptions are applied immediately when added or removed.
// Styles are the .avail-* block in index.css.
//
// Replaces the previously fully hardcoded 9am-5pm-for-every-doctor slot
// generation on the backend — see DoctorAvailabilityService.

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { doctorAvailabilityAPI, navigatorAvailabilityAPI } from '../services/api';

import Icon from './Icon';
import PageHead from './PageHead';
import OverflowMenu from './OverflowMenu';

const DAY_LABELS = {
  1: 'Monday', 2: 'Tuesday', 3: 'Wednesday', 4: 'Thursday',
  5: 'Friday', 6: 'Saturday', 7: 'Sunday',
};
const DAY_ORDER = [1, 2, 3, 4, 5, 6, 7];
const WEEKDAYS = [1, 2, 3, 4, 5];

const DEFAULT_RANGE = { startTime: '09:00', endTime: '17:00' };

const EVENT_META = {
  WEEKLY_SCHEDULE_UPDATED: { icon: 'calendar', label: 'Weekly schedule updated' },
  OVERRIDE_SET:            { icon: 'ban', label: 'Date override set' },
  OVERRIDE_CLEARED:        { icon: 'clock', label: 'Date override cleared' },
};

function formatWhen(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

// "09:00:00" (backend) or "09:00" (input[type=time]) -> "09:00"
function toInputTime(t) {
  return t ? t.slice(0, 5) : '';
}

function toMinutes(t) {
  if (!t) return NaN;
  const [h, m] = t.split(':').map(Number);
  return h * 60 + m;
}

function fromMinutes(total) {
  const t = Math.max(0, Math.min(23 * 60 + 59, total));
  return `${String(Math.floor(t / 60)).padStart(2, '0')}:${String(t % 60).padStart(2, '0')}`;
}

// "13:30" -> "1:30 PM" (padHour: "01:30 PM", the select's face)
function formatTime(t, padHour = false) {
  if (!t) return '';
  const [h, m] = t.split(':').map(Number);
  const hour12 = h % 12 === 0 ? 12 : h % 12;
  const hh = padHour ? String(hour12).padStart(2, '0') : String(hour12);
  return `${hh}:${String(m).padStart(2, '0')} ${h < 12 ? 'AM' : 'PM'}`;
}

// 30-minute steps for the time pickers.
const TIME_STEPS = Array.from({ length: 48 }, (_, i) => fromMinutes(i * 30));

function formatHours(minutes) {
  const h = Math.round((minutes / 60) * 10) / 10;
  return Number.isInteger(h) ? String(h) : h.toFixed(1);
}

// "2026-10-28" -> { day: '28', month: 'OCT', weekday: 'Wednesday' }. Built from
// the parts, not new Date('YYYY-MM-DD'), which is UTC midnight and lands on the
// previous day west of Greenwich.
function dateParts(iso) {
  const [y, m, d] = (iso || '').split('-').map(Number);
  if (!y || !m || !d) return { day: '', month: '', weekday: '' };
  const date = new Date(y, m - 1, d);
  return {
    day: String(d).padStart(2, '0'),
    month: date.toLocaleDateString('en-GB', { month: 'short' }).toUpperCase(),
    weekday: date.toLocaleDateString('en-GB', { weekday: 'long' }),
  };
}

// "GMT+1 · London" for the viewer's own clock — the schedule is shown in local time.
function localZone() {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
    const offset = new Intl.DateTimeFormat('en-GB', { timeZone: tz, timeZoneName: 'shortOffset' })
      .formatToParts(new Date()).find(p => p.type === 'timeZoneName')?.value || '';
    return { offset, city: tz.split('/').pop().replace(/_/g, ' ') };
  } catch {
    return { offset: '', city: '' };
  }
}

function serialise(ranges) {
  return JSON.stringify(
    [...ranges]
      .map(r => [r.dayOfWeek, r.startTime, r.endTime])
      .sort((a, b) => a[0] - b[0] || String(a[1]).localeCompare(String(b[1]))),
  );
}

let nextClientId = 1;

// A time picker: 30-minute steps, plus the current value when it falls between
// steps (the backend accepts any minute, so a saved 16:30/16:45 must still show).
function TimeSelect({ label, value, onChange, invalid }) {
  const options = TIME_STEPS.includes(value) || !value ? TIME_STEPS : [...TIME_STEPS, value].sort();
  return (
    <span className={`avail-time${invalid ? ' is-invalid' : ''}`}>
      <Icon name="clock" size={16} className="avail-time__icon" />
      <select className="form-select avail-time__select" aria-label={label} aria-invalid={invalid || undefined}
        value={value} onChange={e => onChange(e.target.value)}>
        {options.map(t => <option key={t} value={t}>{formatTime(t, true)}</option>)}
      </select>
    </span>
  );
}

export default function DoctorAvailabilityEditor({
  mode, doctorId, kind = 'doctor',
  eyebrow = 'Scheduling', title = 'My availability',
  subtitle = 'Set the hours patients can book with you and plan time away.',
}) {
  const source = kind === 'navigator' ? navigatorAvailabilityAPI : doctorAvailabilityAPI;
  const api = mode === 'self'
    ? {
        getWeekly:     source.getMyWeekly,
        setWeekly:     source.setMyWeekly,
        getOverrides:  source.getMyOverrides,
        setOverride:   source.setMyOverride,
        clearOverride: source.clearMyOverride,
        getAuditLog:   source.getMyAuditLog,
      }
    : {
        getWeekly:     ()               => source.getWeekly(doctorId),
        setWeekly:     (data)           => source.setWeekly(doctorId, data),
        getOverrides:  (params)         => source.getOverrides(doctorId, params),
        setOverride:   (date, data)     => source.setOverride(doctorId, date, data),
        clearOverride: (date)           => source.clearOverride(doctorId, date),
        getAuditLog:   (params)         => source.getAuditLog(doctorId, params),
      };

  const zone = useMemo(localZone, []);

  // ── Weekly schedule ──────────────────────────────────────────────────────
  const [ranges, setRanges]     = useState([]); // [{ clientId, dayOfWeek, startTime, endTime }]
  const [savedKey, setSavedKey] = useState('[]'); // serialise() of what the server has
  const [weeklyLoading, setWeeklyLoading] = useState(true);
  const [weeklySaving,  setWeeklySaving]  = useState(false);
  const [weeklyMsg,     setWeeklyMsg]     = useState({ type: '', text: '' });
  // A day switched off keeps its ranges here so switching it back on restores them.
  const stash = useRef({});

  const loadWeekly = useCallback(() => {
    setWeeklyLoading(true);
    api.getWeekly()
      .then(r => {
        const loaded = (r.data?.ranges || []).map(rg => ({
          clientId: nextClientId++, dayOfWeek: rg.dayOfWeek,
          startTime: toInputTime(rg.startTime), endTime: toInputTime(rg.endTime),
        }));
        setRanges(loaded);
        setSavedKey(serialise(loaded));
      })
      .catch(() => setWeeklyMsg({ type: 'error', text: 'Could not load the weekly schedule.' }))
      .finally(() => setWeeklyLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doctorId, mode, kind]);

  useEffect(() => { loadWeekly(); }, [loadWeekly]);

  const rangesByDay = useMemo(() => {
    const map = {};
    DAY_ORDER.forEach(d => { map[d] = []; });
    ranges.forEach(r => { (map[r.dayOfWeek] = map[r.dayOfWeek] || []).push(r); });
    return map;
  }, [ranges]);

  const isInvalid = r => !(toMinutes(r.endTime) > toMinutes(r.startTime));
  const hasInvalid = ranges.some(isInvalid);
  const dirty = serialise(ranges) !== savedKey;

  const totalMinutes = ranges.reduce((sum, r) => (isInvalid(r) ? sum : sum + toMinutes(r.endTime) - toMinutes(r.startTime)), 0);
  const workingDays = DAY_ORDER.filter(d => rangesByDay[d].length > 0).length;

  function setDayOn(day, on) {
    setWeeklyMsg({ type: '', text: '' });
    if (on) {
      const restored = stash.current[day];
      delete stash.current[day];
      setRanges(rs => [
        ...rs,
        ...(restored?.length ? restored : [{ ...DEFAULT_RANGE }]).map(r => ({ ...r, clientId: nextClientId++, dayOfWeek: day })),
      ]);
    } else {
      stash.current[day] = rangesByDay[day].map(({ startTime, endTime }) => ({ startTime, endTime }));
      setRanges(rs => rs.filter(r => r.dayOfWeek !== day));
    }
  }

  // A new range starts where the day's last one ends, so "Add hours" after
  // 09:00–12:00 offers 12:00–14:00 rather than a duplicate of the first.
  function addRange(dayOfWeek) {
    const last = rangesByDay[dayOfWeek].reduce((m, r) => Math.max(m, toMinutes(r.endTime) || 0), 0);
    const start = last > 0 ? Math.min(last, 22 * 60) : toMinutes(DEFAULT_RANGE.startTime);
    setRanges(rs => [...rs, {
      clientId: nextClientId++, dayOfWeek, startTime: fromMinutes(start), endTime: fromMinutes(start + 120),
    }]);
  }
  function removeRange(clientId) {
    setRanges(rs => rs.filter(r => r.clientId !== clientId));
  }
  function updateRange(clientId, field, value) {
    setRanges(rs => rs.map(r => (r.clientId === clientId ? { ...r, [field]: value } : r)));
  }

  function copyMondayToWeekdays() {
    const monday = rangesByDay[1];
    if (monday.length === 0) return;
    setWeeklyMsg({ type: '', text: '' });
    setRanges(rs => [
      ...rs.filter(r => !WEEKDAYS.includes(r.dayOfWeek) || r.dayOfWeek === 1),
      ...WEEKDAYS.slice(1).flatMap(day => monday.map(m => ({
        clientId: nextClientId++, dayOfWeek: day, startTime: m.startTime, endTime: m.endTime,
      }))),
    ]);
    WEEKDAYS.slice(1).forEach(d => { delete stash.current[d]; });
  }

  async function saveWeekly() {
    setWeeklySaving(true);
    setWeeklyMsg({ type: '', text: '' });
    try {
      await api.setWeekly({
        ranges: ranges.map(r => ({ dayOfWeek: r.dayOfWeek, startTime: r.startTime, endTime: r.endTime })),
      });
      setSavedKey(serialise(ranges));
      setWeeklyMsg({ type: 'success', text: 'Weekly schedule saved.' });
    } catch (err) {
      setWeeklyMsg({ type: 'error', text: err.response?.data?.message || 'Could not save the weekly schedule.' });
    } finally {
      setWeeklySaving(false);
    }
  }

  // ── Overrides ────────────────────────────────────────────────────────────
  const [overrides, setOverrides]   = useState([]);
  const [overridesLoading, setOverridesLoading] = useState(true);
  const [ovOpen,   setOvOpen]       = useState(false);
  const [ovDate,   setOvDate]       = useState('');
  const [ovBlocked, setOvBlocked]   = useState(true);
  const [ovStart,  setOvStart]      = useState('10:00');
  const [ovEnd,    setOvEnd]        = useState('14:00');
  const [ovReason, setOvReason]     = useState('');
  const [ovSaving, setOvSaving]     = useState(false);
  const [ovMsg,    setOvMsg]        = useState({ type: '', text: '' });

  const loadOverrides = useCallback(() => {
    setOverridesLoading(true);
    const from = new Date().toISOString().slice(0, 10);
    const to = new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
    api.getOverrides({ from, to })
      .then(r => setOverrides(Array.isArray(r.data) ? r.data : []))
      .catch(() => setOvMsg({ type: 'error', text: 'Could not load overrides.' }))
      .finally(() => setOverridesLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doctorId, mode, kind]);

  useEffect(() => { loadOverrides(); }, [loadOverrides]);

  function toggleOvForm() {
    setOvOpen(o => !o);
    setOvMsg({ type: '', text: '' });
  }

  async function saveOverride(e) {
    e.preventDefault();
    if (!ovDate) { setOvMsg({ type: 'error', text: 'Pick a date first.' }); return; }
    if (!ovBlocked && !(toMinutes(ovEnd) > toMinutes(ovStart))) {
      setOvMsg({ type: 'error', text: 'The end time must be after the start time.' });
      return;
    }
    setOvSaving(true);
    setOvMsg({ type: '', text: '' });
    try {
      const body = ovBlocked
        ? { blocked: true, ranges: [], reason: ovReason || null }
        : { blocked: false, ranges: [{ startTime: ovStart, endTime: ovEnd }], reason: ovReason || null };
      await api.setOverride(ovDate, body);
      setOvMsg({ type: 'success', text: `Saved for ${ovDate}.` });
      setOvDate('');
      setOvReason('');
      setOvOpen(false);
      loadOverrides();
    } catch (err) {
      setOvMsg({ type: 'error', text: err.response?.data?.message || 'Could not save this override.' });
    } finally {
      setOvSaving(false);
    }
  }

  async function handleClearOverride(date) {
    try {
      await api.clearOverride(date);
      setOvMsg({ type: '', text: '' });
      loadOverrides();
    } catch {
      setOvMsg({ type: 'error', text: `Could not clear the override for ${date}.` });
    }
  }

  // ── Change history ───────────────────────────────────────────────────────
  const [historyOpen, setHistoryOpen]   = useState(false);
  const [history, setHistory]           = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  function toggleHistory() {
    const opening = !historyOpen;
    setHistoryOpen(opening);
    if (opening && history.length === 0) {
      setHistoryLoading(true);
      api.getAuditLog({ page: 0, size: 20 })
        .then(r => setHistory(r.data?.content || []))
        .catch(() => {})
        .finally(() => setHistoryLoading(false));
    }
  }

  const saveDisabled = weeklyLoading || weeklySaving || !dirty || hasInvalid;

  return (
    <div className="avail-page">
      <PageHead
        eyebrow={eyebrow}
        title={title}
        subtitle={subtitle}
        actions={(
          <button type="button" className="btn btn-primary" disabled={saveDisabled} onClick={saveWeekly}>
            <Icon name="check" size={16} /> {weeklySaving ? 'Saving…' : 'Save changes'}
          </button>
        )}
      />

      {weeklyMsg.text && (
        <div className={`alert alert-${weeklyMsg.type === 'error' ? 'error' : 'success'}`} role={weeklyMsg.type === 'error' ? 'alert' : 'status'}>
          {weeklyMsg.text}
        </div>
      )}

      <div className="avail-layout">
        <div className="avail-main">
          {/* ── Weekly hours ── */}
          <section className="card avail-card" aria-labelledby="avail-weekly-title">
            <header className="avail-card__head">
              <div>
                <h2 className="avail-card__title" id="avail-weekly-title">Weekly hours</h2>
                <p className="avail-card__sub">Your recurring schedule, shown in your local time.</p>
              </div>
              {(zone.offset || zone.city) && (
                <div className="avail-zone" title="Times are shown in your device's time zone">
                  {zone.offset && <span className="avail-zone__offset">{zone.offset}</span>}
                  {zone.city && <span className="avail-zone__city">{zone.city}</span>}
                </div>
              )}
            </header>

            {weeklyLoading ? (
              <div className="avail-card__body">
                <div className="skeleton" style={{ height: 320, borderRadius: 'var(--radius-sm)' }} />
              </div>
            ) : (
              <>
                <ul className="avail-days">
                  {DAY_ORDER.map(day => {
                    const dayRanges = rangesByDay[day];
                    const on = dayRanges.length > 0;
                    return (
                      <li key={day} className={`avail-day${on ? ' is-on' : ''}`}>
                        <div className="avail-day__who">
                          <button type="button" role="switch" className="switch" aria-checked={on}
                            aria-label={`${DAY_LABELS[day]} available`} onClick={() => setDayOn(day, !on)} />
                          <span className="avail-day__text">
                            <span className="avail-day__name">{DAY_LABELS[day]}</span>
                            <span className="avail-day__state">{on ? 'Available' : 'Unavailable'}</span>
                          </span>
                        </div>

                        {on ? (
                          <>
                            <div className="avail-ranges">
                              {dayRanges.map((r, i) => {
                                const bad = isInvalid(r);
                                return (
                                  <div key={r.clientId} className="avail-range-wrap">
                                    <div className="avail-range">
                                      <TimeSelect label={`${DAY_LABELS[day]} from`} value={r.startTime} invalid={bad}
                                        onChange={v => updateRange(r.clientId, 'startTime', v)} />
                                      <span className="avail-range-sep">to</span>
                                      <TimeSelect label={`${DAY_LABELS[day]} to`} value={r.endTime} invalid={bad}
                                        onChange={v => updateRange(r.clientId, 'endTime', v)} />
                                      {i > 0 && (
                                        <button type="button" className="avail-icon-btn avail-icon-btn--danger"
                                          aria-label={`Remove ${formatTime(r.startTime)} to ${formatTime(r.endTime)} on ${DAY_LABELS[day]}`}
                                          onClick={() => removeRange(r.clientId)}>
                                          <Icon name="trash" size={18} />
                                        </button>
                                      )}
                                    </div>
                                    {bad && <div className="avail-range-error" role="alert">End time must be after the start time.</div>}
                                  </div>
                                );
                              })}
                            </div>
                            <button type="button" className="btn btn-ghost avail-add-btn" onClick={() => addRange(day)}>
                              <Icon name="plus" size={16} /> Add hours
                            </button>
                          </>
                        ) : (
                          <div className="avail-day__off">No appointments can be booked</div>
                        )}
                      </li>
                    );
                  })}
                </ul>

                <footer className="avail-card__foot">
                  <button type="button" className="btn btn-ghost avail-copy-btn" disabled={rangesByDay[1].length === 0}
                    onClick={copyMondayToWeekdays}>
                    <Icon name="document" size={16} /> Copy Monday to weekdays
                  </button>
                  <span className="avail-card__total">{formatHours(totalMinutes)} bookable hours per week</span>
                </footer>
              </>
            )}
          </section>
        </div>

        <aside className="avail-aside">
          <div className="avail-summary" aria-live="polite">
            <span className="avail-summary__icon" aria-hidden="true"><Icon name="clock" size={22} /></span>
            <div>
              <div className="avail-summary__label">Each week</div>
              <div className="avail-summary__value">{formatHours(totalMinutes)} hours</div>
              <div className="avail-summary__note">
                {workingDays === 0 ? 'No working days set' : `Across ${workingDays} working ${workingDays === 1 ? 'day' : 'days'}`}
              </div>
            </div>
          </div>

          <div className="avail-tip">
            <span className="avail-tip__icon" aria-hidden="true"><Icon name="bolt" size={18} /></span>
            <div>
              <div className="avail-tip__title">Keep your calendar accurate</div>
              <p className="avail-tip__text">Add time off below to prevent new appointments on days you’re away.</p>
            </div>
          </div>
        </aside>
      </div>

      {/* ── Time off & custom hours ── */}
      <section className="card avail-card" aria-labelledby="avail-ov-title">
        <header className="avail-card__head">
          <div>
            <h2 className="avail-card__title" id="avail-ov-title">Time off &amp; custom hours</h2>
            <p className="avail-card__sub">Override your weekly hours for a specific date.</p>
          </div>
          <button type="button" className="btn btn-ghost" aria-expanded={ovOpen} onClick={toggleOvForm}>
            {ovOpen ? <>✕ Cancel</> : <><Icon name="plus" size={16} /> Add exception</>}
          </button>
        </header>

        <div className="avail-card__body">
          {ovOpen && (
            <form onSubmit={saveOverride} className="avail-ov-form">
              <div className="form-group">
                <label className="form-label" htmlFor="avail-ov-date">Date</label>
                <input id="avail-ov-date" type="date" className="form-input" value={ovDate}
                  min={new Date().toISOString().slice(0, 10)} onChange={e => setOvDate(e.target.value)} />
              </div>
              <div className="form-group avail-ov-form__reason">
                <label className="form-label" htmlFor="avail-ov-reason">
                  Reason <span className="avail-optional">Optional</span>
                </label>
                <input id="avail-ov-reason" className="form-input" value={ovReason}
                  onChange={e => setOvReason(e.target.value)} placeholder="e.g. Personal leave" />
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="avail-ov-type">Availability</label>
                <select id="avail-ov-type" className="form-select" value={ovBlocked ? 'blocked' : 'hours'}
                  onChange={e => setOvBlocked(e.target.value === 'blocked')}>
                  <option value="blocked">Unavailable all day</option>
                  <option value="hours">Custom hours</option>
                </select>
              </div>
              <button type="submit" className="btn btn-primary avail-ov-form__submit" disabled={ovSaving}>
                {ovSaving ? 'Saving…' : 'Add exception'}
              </button>
              {!ovBlocked && (
                <div className="avail-ov-form__hours">
                  <TimeSelect label="Custom hours from" value={ovStart} onChange={setOvStart} />
                  <span className="avail-range-sep">to</span>
                  <TimeSelect label="Custom hours to" value={ovEnd} onChange={setOvEnd} />
                </div>
              )}
            </form>
          )}

          {ovMsg.text && (
            <div className={`alert alert-${ovMsg.type === 'error' ? 'error' : 'success'}`} role={ovMsg.type === 'error' ? 'alert' : 'status'}>
              {ovMsg.text}
            </div>
          )}

          {overridesLoading ? (
            <div className="skeleton" style={{ height: 60, borderRadius: 'var(--radius-sm)' }} />
          ) : overrides.length === 0 ? (
            <div className="avail-empty">No days off or custom hours in the next 90 days.</div>
          ) : (
            <ul className="avail-ov-list">
              {overrides.map(o => {
                const d = dateParts(o.date);
                const what = o.blocked
                  ? 'Unavailable all day'
                  : `Available ${(o.ranges || []).map(r => `${formatTime(toInputTime(r.startTime))} – ${formatTime(toInputTime(r.endTime))}`).join(', ')}`;
                return (
                  <li key={o.date} className="avail-ov-row">
                    <div className="avail-ov-date" aria-hidden="true">
                      <span className="avail-ov-date__day">{d.day}</span>
                      <span className="avail-ov-date__month">{d.month}</span>
                    </div>
                    <div className="avail-ov-text">
                      <div className="avail-ov-title">{o.reason || (o.blocked ? 'Time off' : 'Custom hours')}</div>
                      <div className="avail-ov-what">{d.weekday} · {what}</div>
                    </div>
                    <span className={`avail-tag ${o.blocked ? 'avail-tag--off' : 'avail-tag--custom'}`}>
                      {o.blocked ? 'Time off' : 'Custom hours'}
                    </span>
                    <OverflowMenu
                      label={`Actions for ${o.date}`}
                      items={[{ label: 'Remove exception', danger: true, onSelect: () => handleClearOverride(o.date) }]}
                    />
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </section>

      {/* ── Change history ── */}
      <section className="card avail-card">
        <button type="button" className="avail-history-toggle" aria-expanded={historyOpen} onClick={toggleHistory}>
          <span className="avail-card__title">Change history</span>
          <Icon name="chevronRight" size={18} className="avail-history-chevron" />
        </button>
        {historyOpen && (
          <div className="avail-card__body avail-history">
            {historyLoading ? (
              <div className="skeleton" style={{ height: 100, borderRadius: 'var(--radius-sm)' }} />
            ) : history.length === 0 ? (
              <div className="avail-empty">No changes recorded yet.</div>
            ) : (
              history.map((e, i) => {
                const meta = EVENT_META[e.eventType] || { icon: 'clock', label: e.eventType };
                return (
                  <div key={i} className="avail-history-item">
                    <span className="avail-history-item__icon" aria-hidden="true"><Icon name={meta.icon} size={16} /></span>
                    <div className="avail-history-item__main">
                      <div className="avail-history-item__top">
                        <span className="avail-history-item__label">{meta.label}</span>
                        <span className="avail-history-item__when">{formatWhen(e.createdAt)}</span>
                      </div>
                      {e.comment && <div className="avail-history-item__comment">{e.comment}</div>}
                      <div className="avail-history-item__by">
                        by {e.actorType === 'DOCTOR' ? 'the doctor' : 'a navigator'}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        )}
      </section>
    </div>
  );
}
